/*
 * voomi-track.js: shared search A/B tracking helper (spec v1, see EVENT_SPEC.md)
 *
 * Load on BOTH themes, in <head> of layout/theme.liquid, before any search code:
 *   <script src="{{ 'voomi-track.js' | asset_url }}"></script>
 *
 * What it does:
 *   1. Publishes voomi_page_viewed on every page (the exposure event).
 *   2. Exposes window.voomiTrack.* so search code (or the Searchanise adapter)
 *      can publish the other spec events with common fields attached.
 *   3. Stamps the arm onto the cart as the hidden attribute _search_arm.
 *
 * Optional config, set BEFORE this file loads:
 *   window.voomiTrackConfig = {
 *     debug: false,      // console.log every event
 *     stampCart: true,   // write _search_arm to the cart
 *     transport: null    // function(name, payload): replaces Shopify.analytics.publish (tests only)
 *   };
 */
(function () {
  'use strict';
  if (window.voomiTrack) return;
  try { if (window.top !== window.self) return; } catch (e) { return; } // main document only, never iframes

  var SPEC_VERSION = 1;
  var CART_ATTR = '_search_arm';
  var cfg = window.voomiTrackConfig || {};
  if (cfg.stampCart === undefined) cfg.stampCart = true;

  // Read lazily: Shopify.theme is defined by content_for_header, which may run after this file.
  function arm() {
    var t = window.Shopify && window.Shopify.theme;
    return t && t.id ? String(t.id) : 'unknown';
  }
  function themeName() {
    var t = window.Shopify && window.Shopify.theme;
    return (t && t.name) || '';
  }

  // Results-page routes. Override with voomiTrackConfig.searchPaths if the new search uses another route.
  var SEARCH_PATHS = cfg.searchPaths || ['/search', '/pages/search-results-page'];

  function pageType() {
    var p = location.pathname.replace(/\/+$/, '') || '/';
    for (var i = 0; i < SEARCH_PATHS.length; i++) if (p === SEARCH_PATHS[i]) return 'search';
    if (p.indexOf('/products/') !== -1) return 'product'; // includes /collections/x/products/y
    if (p.indexOf('/collections') === 0) return 'collection';
    if (p === '/' || p === '') return 'home';
    if (p.indexOf('/cart') === 0) return 'cart';
    return 'other';
  }

  function publish(shortName, data) {
    var name = 'voomi_' + shortName;
    var payload = {
      spec_version: SPEC_VERSION,
      arm: arm(),
      theme_name: themeName(),
      page_type: pageType(),
      page_path: location.pathname
    };
    if (data) for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
    try {
      if (typeof cfg.transport === 'function') {
        cfg.transport(name, payload);
      } else if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* tracking must never break the page */ }
    if (cfg.debug && window.console) console.log('[voomiTrack]', name, payload);
  }

  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function int(v) { var n = parseInt(v, 10); return isNaN(n) ? null : n; }
  var SHOWN_AS = ['results', 'catalog_choices', 'none'];

  window.voomiTrack = {
    pageType: pageType,
    arm: arm,

    // A results set was shown for a query. Not for filter/sort/pagination changes.
    // resultCount = exact matches (unchanged, keeps zero_result comparable over time).
    // Optional (v2, 10/27/2026): shownAs = what the page actually showed, 'results' |
    // 'catalog_choices' | 'none', and shownCount = products on the page, all sections. They
    // ride in item_type / item_text (blank on this event until now), so the pixel, receiver
    // and table need no change.
    searchSubmitted: function (o) {
      var count = int(o.resultCount);
      var shownAs = SHOWN_AS.indexOf(o.shownAs) !== -1 ? o.shownAs : '';
      var shownCount = int(o.shownCount);
      publish('search_submitted', {
        query: str(o.query).trim(),
        result_count: count,
        zero_result: count === 0,
        item_type: shownAs,
        item_text: shownCount === null ? '' : String(shownCount)
      });
    },

    // Click on a product tile in search results or a collection grid.
    resultClicked: function (o) {
      publish('search_result_clicked', {
        product_id: str(o.productId),
        position: int(o.position),
        page: int(o.page) || 1,
        surface: o.surface || (pageType() === 'collection' ? 'collection' : 'search'),
        query: str(o.query).trim()
      });
    },

    // Click on any item in the search dropdown.
    autocompleteUsed: function (o) {
      publish('autocomplete_used', {
        query: str(o.query).trim(),
        item_type: o.itemType || 'other',
        item_text: str(o.itemText).trim().slice(0, 200),
        product_id: str(o.productId),
        position: int(o.position)
      });
    },

    // A filter value was added (not removed).
    filterApplied: function (o) {
      publish('filter_applied', {
        filter_name: str(o.filterName),
        filter_value: str(o.filterValue),
        surface: o.surface || (pageType() === 'collection' ? 'collection' : 'search'),
        query: str(o.query).trim()
      });
    },

    _publish: publish // escape hatch for future events
  };

  // ---- cart stamp ----------------------------------------------------------
  // Check the cart on page load, and again after anything is added to the cart
  // (a cart can be created mid-page by an AJAX add, e.g. a drawer or quick-add).
  var stamping = false;
  function stampCart() {
    if (!cfg.stampCart || stamping || !window.fetch) return;
    var want = arm();
    if (want === 'unknown') return;
    stamping = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Empty cart: nothing to order yet, and don't create carts for every visitor.
        if (!cart || !cart.item_count) return;
        if (cart.attributes && cart.attributes[CART_ATTR] === want) return;
        var attrs = {}; attrs[CART_ATTR] = want;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      })
      .catch(function () {})
      .then(function () { stamping = false; });
  }

  function isCartAdd(url) { return /\/cart\/add(\.js)?(\?|$)/.test(str(url)); }

  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var p = _fetch.apply(this, arguments);
      if (isCartAdd(url)) p.then(function () { setTimeout(stampCart, 0); }, function () {});
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    var _open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartAdd(url)) this.addEventListener('load', function () { setTimeout(stampCart, 0); });
      return _open.apply(this, arguments);
    };
  }

  // ---- exposure ------------------------------------------------------------
  function onReady() {
    publish('page_viewed', null);
    stampCart();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();
